import React, { useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './MiniCart.css';
const MiniCart = () => {
  const {
    cartData,
    isMiniCartOpen,
    closeMiniCart,
    updateQuantity,
    removeItem,
    loading,
    getCartItemsCount
  } = useCart();
  const {
    token,
    isAuthenticated,
  } = useAuth();

  useEffect(() => {
    if (isMiniCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMiniCartOpen]);

  const handleCheckout = () => {
    const cartId = cartData?.id;
    if (!cartId) return;

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/checkout/index/index";

    const quoteInput = document.createElement("input");
    quoteInput.type = "hidden";
    quoteInput.name = "quote_id";
    quoteInput.value = cartId;
    form.appendChild(quoteInput);

    if (isAuthenticated) {
      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "customer_token";
      tokenInput.value = token;
      form.appendChild(tokenInput);
    }

    document.body.appendChild(form);
    form.submit();
  };

  if (!isMiniCartOpen) return null;

  const itemsCount = getCartItemsCount();
  const subtotal = cartData?.prices?.subtotal_excluding_tax?.value || 0;
  const subtotalIncTax = cartData?.prices?.subtotal_including_tax?.value || 0;
  const total = cartData?.prices?.grand_total?.value || 0;
  const currency = cartData?.prices?.grand_total?.currency || 'USD';
  const appliedTaxes = cartData?.prices?.applied_taxes || [];
  const discounts = cartData?.prices?.discounts || [];
  const shippingMethod = cartData?.shipping_addresses?.[0]?.selected_shipping_method;

  const totalTaxAmount = appliedTaxes.reduce((sum, tax) => sum + (tax.amount?.value || 0), 0);
  const totalDiscountAmount = discounts.reduce((sum, discount) => sum + (discount.amount?.value || 0), 0);
  const shippingAmount = shippingMethod?.amount?.value || 0;

  return (
    <>
      {/* Overlay */}
      <div
        className="mini-cart-overlay"
        onClick={closeMiniCart}
      />

      {/* Mini Cart Panel */}
      <div className="mini-cart-panel">
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Shopping Cart ({itemsCount})
          </h2>
          <button
            onClick={closeMiniCart}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Cart Items */}
        <div style={{
          flex: 1,
          padding: '1rem',
          overflowY: 'auto'
        }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              Loading...
            </div>
          )}

          {!loading && (!cartData?.items || cartData.items.length === 0) && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              Your cart is empty
            </div>
          )}

          {!loading && cartData?.items?.map((item) => (
            <div key={item.id} style={{
              display: 'flex',
              padding: '1rem 0',
              borderBottom: '1px solid #f3f4f6'
            }}>
              <img
                src={item.product.small_image?.url || '/placeholder.jpg'}
                alt={item.product.name}
                style={{
                  width: '60px',
                  height: '60px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  marginRight: '0.75rem'
                }}
              />

              <div style={{ flex: 1 }}>
                <h4 style={{
                  margin: '0 0 0.5rem 0',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  lineHeight: '1.2'
                }}>
                  {item.product.name}
                </h4>

                <div style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginBottom: '0.5rem'
                }}>
                  SKU: {item.product.sku}
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <button
                      onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                      style={{
                        width: '24px',
                        height: '24px',
                        border: '1px solid #d1d5db',
                        borderRadius: '2px',
                        background: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px'
                      }}
                    >
                      −
                    </button>

                    <span style={{
                      minWidth: '24px',
                      textAlign: 'center',
                      fontSize: '0.875rem'
                    }}>
                      {item.quantity}
                    </span>

                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      style={{
                        width: '24px',
                        height: '24px',
                        border: '1px solid #d1d5db',
                        borderRadius: '2px',
                        background: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px'
                      }}
                    >
                      +
                    </button>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '0.25rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                        ${item.prices?.price?.value?.toFixed(2) || '0.00'}
                      </span>

                      <button
                        onClick={() => removeItem(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          padding: '0.25rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    {/* Item discount */}
                    {item.prices?.total_item_discount?.value > 0 && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#059669',
                        fontWeight: '500'
                      }}>
                        Discount: -${item.prices.total_item_discount.value.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer with totals and checkout */}
        {cartData?.items && cartData.items.length > 0 && (
          <div style={{
            padding: '1.5rem',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            {/* Subtotal */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Subtotal:
              </span>
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                ${subtotal.toFixed(2)} {currency}
              </span>
            </div>

            {/* Taxes */}
            {totalTaxAmount > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Tax:
                </span>
                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  ${totalTaxAmount.toFixed(2)} {currency}
                </span>
              </div>
            )}

            {/* Discounts */}
            {totalDiscountAmount > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Discount:
                </span>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#059669' }}>
                  -${totalDiscountAmount.toFixed(2)} {currency}
                </span>
              </div>
            )}

            {/* Shipping */}
            {shippingMethod && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.125rem'
                }}>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Shipping:
                  </span>
                  {shippingMethod.method_title && (
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {shippingMethod.carrier_title} - {shippingMethod.method_title}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  {shippingAmount > 0 ? `$${shippingAmount.toFixed(2)} ${currency}` : 'Free'}
                </span>
              </div>
            )}

            {/* Separator */}
            {(totalTaxAmount > 0 || totalDiscountAmount > 0 || shippingMethod) && (
              <div style={{
                borderTop: '1px solid #e5e7eb',
                marginBottom: '0.5rem',
                paddingTop: '0.5rem'
              }} />
            )}

            {/* Grand Total */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              fontSize: '1.125rem',
              fontWeight: '600'
            }}>
              <span>Total:</span>
              <span>${total.toFixed(2)} {currency}</span>
            </div>

            <button
              onClick={handleCheckout}
              style={{
                width: '100%',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default MiniCart;